import moment, { Moment } from 'moment-timezone'
import { getCountryCode } from 'countries-list'
import timezones from 'timezone-abbreviations'
import dataManager from 'pages/main/data/DataManager'

import ICAL from 'ical.js'

import { rruleDescription } from 'pages/calendar/utils/RRuleDesc'
import { RRule } from 'rrule'

declare global {
  interface Window {
    moment: typeof moment
  }
}

window.moment = moment

// Type definitions
interface ItemValueProp {
  tzid?: string
  cn?: string
  partstat?: string
  'sent-by'?: string
  [key: string]: any
}

interface ICALProperty {
  0: string // property name
  1: ItemValueProp // property parameters
  2: string // value type
  3: any // value
}

interface AttendeeValue {
  email: string
  name?: string
  type?: number
}

interface TimezoneItem {
  abbr: string
  description: string
  names: string[]
}

interface ParsedICSResult {
  vcalendar?: {
    method?: ItemValue
    vevent?: {
      [key: string]: ItemValue | ItemValue[]
      attendee?: ItemValue | ItemValue[]
      organizer?: ItemValue
      dtstart?: ItemValue
      exceptions?: any[]
    }
    vtimezone?: {
      [key: string]: ItemValue
    }
  }
  [key: string]: any
}

interface MeetingBehalfInfo {
  email: string
  name?: string
}

interface MeetingInfo {
  uid: string
  recurrenceId: any
  dt_stamp: number
  dt_start: number
  dt_end: number
  title: string
  location: string
  response_requested: boolean
  allDay: boolean
  timezone: string
  sender: EmailAddressInfo
  disallow_new_time_proposal: boolean
  new_start_propose_time: number | null
  new_end_propose_time: number | null
  meeting_behalf_sent_by: string | null
  meeting_behalf_organizer: MeetingBehalfInfo | null
  required_attendees: AttendeeValue[]
  optional_attendees: AttendeeValue[]
  resource_attendees: AttendeeValue[]
  only_show_status_str: boolean
  is_owner_meeting: boolean
  recurrence: string | null
  sequence: number
  type: number
  enable_response?: boolean
  meeting_response_actions?: number[]
  propose_comment?: string
}

interface EmailAddressInfo {
  email: string
  name?: string
}

interface UserInfo {
  email: string
  [key: string]: any
}

interface GetMeetingInfoOptions {
  from: EmailAddressInfo
  userInfo: UserInfo
}

const getType = (status: string | null | undefined): number => {
  let type = 0

  switch (status) {
    case 'CANCELLED':
      type = 1
      break
    case 'ACCEPTED':
      type = 2
      break
    case 'TENTATIVE':
      type = 3
      break
    case 'DECLINED':
      type = 4
      break
    // case 'CONFIRMED':
    //   type = 5;
    //   break;
    case 'NEEDS-ACTION':
    default:
      type = 0
  }
  return type
}

const getTypeByMethod = (
  method: string | null | undefined,
  status: string | null | undefined,
): number => {
  let type = 0
  switch (method) {
    case 'REPLY':
    case 'COUNTER':
      type = getType(status)
      break
    case 'CANCEL':
      type = 1
      break
    case 'REQUEST':
    default:
      type = 0
  }

  return type
}

const getEmailAddress = (txt: string): string => {
  let email = txt
  if (email.toLowerCase().indexOf('mailto:') === 0) {
    email = email.substring(7)
  }
  return email
}

class ItemValue {
  prop: ItemValueProp
  type: string
  value: any
  owner: ICSParser
  icalPropertyObj: ICALProperty

  constructor(obj: ICALProperty, owner: ICSParser) {
    this.icalPropertyObj = obj
    this.prop = obj[1]
    this.type = obj[2]
    this.value = obj[3]
    this.owner = owner
  }

  getOrigin = (): string => {
    return (ICAL.stringify as any).property(this.icalPropertyObj)
  }

  getValue = (): any => {
    let ret: any = this.value
    const { prop } = this

    switch (this.type) {
      case 'date-time':
      case 'date': {
        const { tzid } = prop
        let time: Moment = moment(this.value)
        const timezone = tzid || this.owner.defaultTimezone
        if (timezone != null && timezone.length > 0) {
          time = time.tz(timezone)
        }
        ret = time.utc().valueOf()
        break
      }
      case 'recur':
        ret = this.value
        break
      case 'cal-address': {
        const email = getEmailAddress(this.value)
        ret = {
          email,
        } as AttendeeValue
        if (prop.cn != null) {
          ret.name = prop.cn
        }
        if (prop.partstat != null) {
          ret.type = getType(prop.partstat)
        }
        break
      }
      case 'text':
      case 'integer':
      default:
        ret = this.value
    }

    return ret
  }
}

class Item {
  obj: ICALProperty
  key: string
  value: ItemValue

  constructor(obj: ICALProperty, owner: ICSParser) {
    this.obj = obj
    this.key = obj[0]
    this.value = new ItemValue(obj, owner)
  }
}

class ICSParser {
  icsRes: any[]
  result: ParsedICSResult
  defaultTimezone: string

  constructor(icsRes: any[]) {
    this.icsRes = icsRes
    this.result = {}
    this.defaultTimezone = ''

    this.init()
  }

  private isItem = (obj: any): obj is ICALProperty => {
    if (
      Array.isArray(obj) === true &&
      obj.length === 4 &&
      typeof obj[0] === 'string' &&
      typeof obj[1] === 'object' &&
      typeof obj[2] === 'string'
    ) {
      return true
    }
    return false
  }

  private getTimezone = (txt: string | null | undefined): string => {
    if (txt != null) {
      let tz = moment.tz.zone(txt)
      if (tz != null) {
        return txt
      }
      const tzArr = moment.tz.zonesForCountry(txt) as string[] | null
      if (tzArr != null && tzArr.length > 0) {
        return tzArr[0]
      }
      const countryCode = getCountryCode(txt)
      if (typeof countryCode === 'string') {
        const tzArr2 = moment.tz.zonesForCountry(countryCode) as string[] | null
        if (tzArr2 != null && tzArr2.length > 0) {
          return tzArr2[0]
        }
      }
      const lowerTxt = txt.toLowerCase()
      const timezone = (timezones as any as TimezoneItem[]).find((item) => {
        return item.abbr.toLowerCase() === lowerTxt || item.description.toLowerCase() === lowerTxt
      })
      if (timezone != null) {
        return timezone.names[0]
      }
    }
    return ''
  }

  private parseObj = (obj: any, ret: any): void => {
    if (this.isItem(obj) === true) {
      const item = new Item(obj, this)
      if (ret[item.key] == null) {
        ret[item.key] = item.value
      } else {
        if (Array.isArray(ret[item.key]) === false) {
          ret[item.key] = [ret[item.key]]
        }
        ret[item.key].push(item.value)
      }
    } else if (Array.isArray(obj) === true) {
      if (typeof obj[0] === 'string') {
        const childRet: any = {}
        const name = obj[0]

        if (ret[name] && name === 'vevent') {
          if (ret[name].exceptions) {
            ret[name].exceptions.push(childRet)
          } else {
            ret[name].exceptions = [childRet]
          }
        } else {
          ret[name] = childRet
        }

        obj.slice(1).forEach((item) => {
          this.parseObj(item, childRet)
        })
      } else {
        obj.forEach((item) => {
          this.parseObj(item, ret)
        })
      }
    }
  }

  private getChild = (obj: any, key: string): any => {
    const key0 = key.toLowerCase()
    if (obj[key0] != null) {
      return obj[key0]
    }
    return null
  }

  parse = (): ParsedICSResult => {
    const ret: ParsedICSResult = {}
    this.parseObj(this.icsRes, ret)

    if (ret.vcalendar != null) {
      const vtimezone = this.getChild(ret.vcalendar, 'VTIMEZONE')
      if (vtimezone != null) {
        const tzid = this.getChild(vtimezone, 'TZID')
        if (tzid != null) {
          this.defaultTimezone = this.getTimezone(tzid.getValue())
        }
      }

      if (ret.vcalendar.vevent?.attendee != null) {
        if (Array.isArray(ret.vcalendar.vevent.attendee) === false) {
          ret.vcalendar.vevent.attendee = [ret.vcalendar.vevent.attendee]
        }
      }
    }

    this.result = ret
    return ret
  }

  getMeetingInfo = (options: GetMeetingInfoOptions): MeetingInfo | null => {
    const res = this.result
    const { method, vevent } = res.vcalendar || {}
    const { from, userInfo } = options

    if (vevent != null) {
      const getValue = (
        key: string,
        defaultValue: any = null,
        defaultType: string = 'text',
      ): any => {
        const key0 = key.toLowerCase()
        if (vevent[key0] != null) {
          const item = vevent[key0] as ItemValue
          if (item.type === null || item.type === 'unknown') {
            item.type = defaultType
          }
          return item.getValue()
        }
        return defaultValue
      }

      const getOrigin = (
        key: string,
        defaultValue: any = null,
        defaultType: string = 'text',
      ): string | null => {
        const key0 = key.toLowerCase()
        if (vevent[key0] != null) {
          const item = vevent[key0] as ItemValue
          if (item.type === null || item.type === 'unknown') {
            item.type = defaultType
          }
          return item.getOrigin()
        }
        return defaultValue
      }

      const sequence = parseInt(getValue('SEQUENCE') || '0')
      const statusTxt = getValue('STATUS')
      const methodTxt = method?.getValue()

      const statusType = getTypeByMethod(methodTxt, statusTxt)

      let type = statusType

      const { attendee, organizer } = vevent

      const req_attendees: AttendeeValue[] = []
      const opt_attendees: AttendeeValue[] = []
      const res_attendees: AttendeeValue[] = []

      if (attendee != null) {
        const attendeeArray = Array.isArray(attendee) ? attendee : [attendee]
        attendeeArray.forEach((item) => {
          const { role, cutype, partstat } = item.prop
          const value = item.getValue()

          // role - default: REQ-PARTICIPANT
          // cutype - default: INDIVIDUAL
          if (role === 'REQ-PARTICIPANT') {
            req_attendees.push(value)
          } else if (role === 'OPT-PARTICIPANT') {
            opt_attendees.push(value)
          } else if (cutype === 'RESOURCE' || cutype === 'ROOM') {
            res_attendees.push(value)
          } else {
            req_attendees.push(value)
          }

          if (methodTxt === 'REPLY' || methodTxt === 'COUNTER') {
            // CONFIRMED
            if (from != null && value.email === from.email) {
              const partType = getType(partstat)
              type = partType
            }
          }
        })
      }

      let meeting_behalf_sent_by: string | null = null
      let meeting_behalf_organizer: MeetingBehalfInfo | null = null

      if (organizer != null) {
        meeting_behalf_organizer = organizer.getValue()
        if (organizer.prop['sent-by'] != null) {
          meeting_behalf_sent_by = getEmailAddress(organizer.prop['sent-by'])
        }
      }

      let disallow_new_time_proposal = false
      if (getValue('X-MICROSOFT-DISALLOW-COUNTER') === 'TRUE') {
        disallow_new_time_proposal = true
      }

      let dt_start: number | null = null
      let dt_end: number | null = null
      let new_start_propose_time: number | null = null
      let new_end_propose_time: number | null = null
      let propose_comment: string | null = null

      if (methodTxt === 'COUNTER') {
        new_start_propose_time = getValue('DTSTART')
        new_end_propose_time = getValue('DTEND')

        dt_start = getValue('X-MS-OLK-ORIGINALSTART', new_start_propose_time, 'date-time')
        dt_end = getValue('X-MS-OLK-ORIGINALEnd', new_end_propose_time, 'date-time')
        propose_comment = getValue('COMMENT')
      } else {
        dt_start = getValue('DTSTART')
        dt_end = getValue('DTEND')
      }

      // DTEND가 없고 DURATION이 있으면 DTSTART + DURATION으로 계산
      if (dt_end == null && dt_start != null) {
        const durationValue = getValue('DURATION')
        if (durationValue != null) {
          try {
            const duration =
              typeof durationValue === 'string'
                ? ICAL.Duration.fromString(durationValue)
                : durationValue
            dt_end = dt_start + duration.toSeconds() * 1000
          } catch (err) {
            console.log(' --- DURATION parse error : ', err)
          }
        }
      }

      const rruleValue = getOrigin('RRULE')

      let recurrence: string | null = null

      if (rruleValue != null && rruleValue.length > 0) {
        const dtStartValue = getOrigin('DTSTART')
        const rruleStr = `${dtStartValue}\n${rruleValue}`
        const rrule = RRule.fromString(rruleStr)
        recurrence = rruleDescription(rrule as any)
      }

      // 참석자 중에 본인이 없으면 response 메뉴 보여주지 않음.
      const enableResponseActions = [...req_attendees, ...opt_attendees, ...res_attendees].some(
        (item) => item.email === userInfo.email,
      )

      const meeting_info: MeetingInfo = {
        uid: getValue('UID'),
        recurrenceId: getValue('RECURRENCE-ID'),
        dt_stamp: getValue('DTSTAMP'),
        dt_start,
        dt_end,
        title: getValue('SUMMARY'),
        location: getValue('LOCATION'),
        response_requested: true, // (method != null && method.getValue() === 'RESPONSE'),
        allDay: vevent.dtstart?.type === 'date',
        timezone: this.defaultTimezone,
        sender: from,
        disallow_new_time_proposal,
        new_start_propose_time,
        new_end_propose_time,
        meeting_behalf_sent_by,
        meeting_behalf_organizer,
        required_attendees: req_attendees,
        optional_attendees: opt_attendees,
        resource_attendees: res_attendees,
        only_show_status_str: false, // 응답 가능 여부.? getValue('ONLY_STATUS')
        // recurrence_str: getValue('RRULE'),
        is_owner_meeting: false, // (statusTxt === 'CONFIRMED'),
        recurrence,
        sequence,
        type,
      }

      if (enableResponseActions === true) {
        meeting_info.enable_response = true

        // 8: 보내기 전에 응답 편집,
        // 16: 지금 응답 보내기,
        // 32: 답장 보지 않기,
        // 128: 새로운 시간 제안.
        // 여기서는 8, 16만 지원.
        meeting_info.meeting_response_actions = [8, 16] // [8, 16, 32, 128],
      }

      if (propose_comment != null) {
        meeting_info.propose_comment = propose_comment
      }

      // meeting_info.type = 2;
      // // meeting_info.sender = { email: 'jungho.kim@officemail.app' };
      // meeting_info.propose_comment = '나는 propose comment다. !!!!, 나는 propose comment다. !!!!, 나는 propose comment다. !!!!, 나는 propose comment다. !!!!, 나는 propose comment다. !!!!, 나는 propose comment다. !!!!';

      return meeting_info
    }

    return null
  }

  init = (): void => {
    this.parse()
  }
}

export const getMeetingInfo = (
  icsText: string | null,
  from: EmailAddressInfo,
): MeetingInfo | null => {
  let meeting_info: MeetingInfo | null = null
  if (icsText != null) {
    try {
      const icsObj = ICAL.parse(icsText)
      // console.log(' --- icsObj : ', icsObj)

      const icsParser = new ICSParser(icsObj)
      // const icsData = icsParser.parse()
      // console.log(' --- icsData : ', icsData, icsParser)

      meeting_info = icsParser.getMeetingInfo({
        from,
        userInfo: dataManager.getUserInfo(),
      })

      // console.log(' --- meeting_info : ', meeting_info)
    } catch (err) {
      console.log(' --- error : ', err)
    }
  }
  return meeting_info
}

export default ICSParser
